import React, { useState, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowUpDown, Grid3x3, List, Search } from "lucide-react";
import { useDispatch } from "react-redux";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";

import { ResourceCard } from "@/modules/hub/features/ResourceLibrary/components/ResourceCard/ResourceCard";
import { ResourceListItem } from "@/modules/hub/features/ResourceLibrary/components/ResourceListItem/ResourceListItem";
import { FilterSidebar } from "@/modules/hub/features/ResourceLibrary/components/FilterSidebar/FilterSidebar/FilterSidebar";
import { FeaturedResources } from "@/modules/hub/features/ResourceLibrary/components/FeaturedResources/FeaturedResources";
import ActiveFiltersHandler from "@/modules/hub/features/ResourceLibrary/components/ActiveFiltersHandler/ActiveFiltersHandler";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import "./ResourceLibrary.css";
import { useResourcesLibrary } from "../../hooks/useResourceLibrary";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileFilterToggleButton from "../MobileFilterToggleButton/MobileFilterToggleButton";
import { ResourcesLibrarySkeleton } from "@/modules/hub/features/ResourceLibrary/components/ResourceLibrary/ResourceLibrarySkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trackResourceLibraryPageViewed } from "../../analytics/resourceLibraryAnalytics";

export function ResourcesLibrary() {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackResourceLibraryPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const dispatch = useDispatch();
  const {
    form,
    resources,
    hasMore,
    totalCount,
    loading,
    isInitialLoading,
    sidebarMetadata,
    isMetadataLoading,
    fetchMore,
    handleClearFilters,
    setFeaturedOnly,
    sortOption,
    setSortOption,
    handleFavoriteToggle,
    refreshFavorites,
  } = useResourcesLibrary();

  const isMobile = useIsMobile();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  type ViewMode = "card" | "list";
  const [viewMode, setViewMode] = useState<ViewMode>("card");

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem("resourceLibraryView") as ViewMode;
    if (savedView === "card" || savedView === "list") {
      setViewMode(savedView);
    }
  }, []);

  // Save view preference to localStorage
  useEffect(() => {
    localStorage.setItem("resourceLibraryView", viewMode);
  }, [viewMode]);

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "mostViewed", label: "Most Viewed" },
    { value: "alphabetical", label: "Title A-Z" },
  ];

  const handleViewAllFeatured = () => {
    setFeaturedOnly();
    // Scroll to top to show the filtered results
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  if (isInitialLoading) {
    return <ResourcesLibrarySkeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <MobileFilterToggleButton
          onClick={() => setIsFilterSidebarOpen(true)}
        />
      }
      subtitle="Access and discover learning materials, documents, videos, and more"
      title="Resource Library"
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div
          className={
            isMobile
              ? undefined
              : "w-80 shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700"
          }
        >
          <FilterSidebar
            filters={sidebarMetadata}
            form={form}
            isLoading={isMetadataLoading}
            isOpen={isFilterSidebarOpen}
            onOpenChange={setIsFilterSidebarOpen}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Featured Resources Section - Only show when no active filters */}
          {!form.watch("isFavoriteOnly") &&
            !form.watch("isFeaturedOnly") &&
            form.watch("trades").length === 0 &&
            form.watch("contentTypes").length === 0 &&
            form.watch("categories").length === 0 &&
            form.watch("businessRoles").length === 0 && (
              <FeaturedResources
                context="library"
                onViewAllFeatured={handleViewAllFeatured}
              />
            )}

          <ActiveFiltersHandler form={form} onClearAll={handleClearFilters} />

          {/* Sort Controls and View Toggle */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {totalCount > 0 && `${totalCount} resources found`}
            </div>
            <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                <Select
                  onValueChange={(value) => setSortOption(value as any)}
                  value={sortOption}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-md p-1">
                <Button
                  className="h-7 w-7 p-0"
                  onClick={() => handleViewModeChange("card")}
                  size="sm"
                  title="Card View"
                  variant={viewMode === "card" ? "secondary" : "ghost"}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  className="h-7 w-7 p-0"
                  onClick={() => handleViewModeChange("list")}
                  size="sm"
                  title="List View"
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <InfiniteScroll
            dataLength={resources.length}
            endMessage={
              resources.length > 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">
                    No more resources to load
                  </p>
                </div>
              ) : null
            }
            hasMore={hasMore}
            loader={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  Loading more...
                </p>
              </div>
            }
            next={fetchMore}
          >
            {loading && resources.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  Searching...
                </p>
              </div>
            ) : resources.length > 0 ? (
              viewMode === "card" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {resources.map((resource) => (
                    <div className="fade-in" key={resource.id}>
                      <ResourceCard
                        onToggleFavorite={handleFavoriteToggle}
                        resource={resource}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {resources.map((resource) => (
                    <div className="fade-in" key={resource.id}>
                      <ResourceListItem
                        onToggleFavorite={handleFavoriteToggle}
                        resource={resource}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
                  No resources found matching your criteria.
                </p>

                {/* Search prompt if no filters are active */}
                {!form.watch("isFavoriteOnly") &&
                  !form.watch("isFeaturedOnly") &&
                  form.watch("trades").length === 0 &&
                  form.watch("contentTypes").length === 0 &&
                  form.watch("categories").length === 0 &&
                  form.watch("businessRoles").length === 0 && (
                    <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg max-w-md mx-auto">
                      <Search className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-300 font-medium mb-2">
                        Looking for something specific?
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Search across all resources and events
                      </p>
                      <button
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                        onClick={() => dispatch(openSearchModal())}
                      >
                        <Search className="h-4 w-4" />
                        Open Search
                        <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-blue-700 rounded">
                          {navigator.platform?.includes("Mac")
                            ? "⌘K"
                            : "Ctrl+K"}
                        </kbd>
                      </button>
                    </div>
                  )}

                {/* Clear filters button if filters are active */}
                {(form.watch("isFavoriteOnly") ||
                  form.watch("isFeaturedOnly") ||
                  form.watch("trades").length > 0 ||
                  form.watch("contentTypes").length > 0 ||
                  form.watch("categories").length > 0 ||
                  form.watch("businessRoles").length > 0) && (
                  <button
                    className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                    onClick={handleClearFilters}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </MainPageWrapper>
  );
}
